import cn from 'classnames';
import type { ComponentChildren, ComponentProps, JSX } from 'preact';
import { useMemo } from 'preact/hooks';
import type { IconType } from '../../types';
import type { HTMLAttributes } from '../../types/utility';
import { EMPTY_ARRAY } from '../../utility';
import { SecondaryButton, SecondaryButtonLink } from '../button';
import { MenuButton } from '../details';
import { UnorderedList } from '../list';
import Navigation from '../navigation';

type NavItems = ComponentProps<typeof Navigation>['items'];
type Client = 'ox' | 'ox-fe';

export type AccountDetailsItem = ComponentProps<typeof MenuButton>['items'][number];

interface BaseProps {
  accountDetailsItems?: AccountDetailsItem[];
  children?: ComponentChildren;
  env?: 'production' | 'development' | 'staging';
  name: string;
  primaryNavItems?: NavItems;
  isSticky?: boolean;
  secondaryNavItems?: (Omit<NavItems[number], 'href'> & {
    href?: string;
    icon?: IconType;
    onClick?: (e: JSX.TargetedEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  })[];
  type?: 'internal' | 'external';
  client?: Client;
  // Override the account-menu Logout target (defaults to the keycloak end-session URL).
  logoutHref?: string;
}

const APP_SUBDOMAINS: Record<string, string> = {
  Transactions: 'mtx',
  Users: 'users',
  Visits: 'visits',
  Mainstay: 'offers',
  Sell: 'sell',
  Mail: 'mail',
};

type Props = BaseProps & HTMLAttributes<HTMLElement>;

const formatLogoutUrl = (
  env: 'production' | 'development' | 'staging',
  name: string,
  client: Client,
) => {
  let postfix = '';
  if (env === 'development') {
    postfix = '-dev';
  } else if (env === 'staging') {
    postfix = '-staging';
  }
  const url = new URL(
    `https://login${postfix}.mainstay.io/realms/mainstay/protocol/openid-connect/logout`,
  );
  url.searchParams.set('client_id', client);
  url.searchParams.set(
    'post_logout_redirect_uri',
    `https://${APP_SUBDOMAINS[name] || 'www'}${postfix}.mainstay.io/logout`,
  );
  return url.toString();
};

// @ts-ignore env exists in Vite env
const MODE = import.meta.env?.MODE;

export default function Header({
  accountDetailsItems,
  children,
  className,
  client = 'ox',
  env,
  logoutHref,
  name,
  primaryNavItems,
  secondaryNavItems,
  isSticky,
  type = 'internal',
}: Props) {
  const classList = cn('app-header', className, {
    sticky: isSticky,
  });

  const logoutUrl = useMemo(() => {
    if (env) {
      return formatLogoutUrl(env, name, client);
    }
    if (MODE) {
      return formatLogoutUrl(MODE, name, client);
    }
    if (typeof location !== 'undefined') {
      const { hostname } = location;
      if (hostname.includes('-staging')) {
        return formatLogoutUrl('staging', name, client);
      } else if (hostname.includes('-dev') || hostname === 'localhost') {
        return formatLogoutUrl('development', name, client);
      }

      return formatLogoutUrl('production', name, client);
    }
    return formatLogoutUrl('production', name, client);
  }, [env, name, client]);

  const imgProps =
    type === 'external'
      ? { src: 'https://mainstay.io/images/mainstay.svg' }
      : { src: 'https://mainstay.io/images/mainstay-logo.svg', width: 20 };

  return (
    <header className={classList}>
      <a href="/" className="logo">
        <img alt="Mainstay" {...imgProps} />
        {type === 'internal' && name}
      </a>

      {primaryNavItems && primaryNavItems.length > 0 && (
        <Navigation
          aria-label="Primary"
          className="primary-nav"
          items={primaryNavItems}
          orientation="horizontal"
          type="unbounded"
        />
      )}

      {children && <div className="app-header-children">{children}</div>}

      {type === 'internal' && (
        <nav aria-label="Secondary" className="secondary-nav">
          <UnorderedList
            items={[
              ...(secondaryNavItems ?? []).map(({ href, label, ...props }) => {
                const key = label;
                const Component = !href ? SecondaryButton : SecondaryButtonLink;

                return (
                  <Component
                    key={key}
                    className="secondary-nav-item"
                    size="small"
                    {...props}
                    href={href}
                  >
                    {label as NonNullable<ComponentChildren>}
                  </Component>
                );
              }),
              <SecondaryButtonLink
                className="secondary-nav-item"
                href="https://dashboard.mainstay.io/"
                icon="apps"
                target="_blank"
                key="secondary-nav-item"
              >
                Apps
              </SecondaryButtonLink>,
              <MenuButton
                className="account-details"
                icon="person"
                text="Account Details"
                hierarchy="tertiary"
                position="bottom-right"
                isIconOnly
                items={[
                  ...(accountDetailsItems ?? EMPTY_ARRAY),
                  // target=_top: logout must reach nginx/keycloak as a real navigation. Same-origin
                  // hrefs like /logout are otherwise intercepted by SPA routers (preact-iso) and
                  // rendered as the app's 404 page (TXN-2902).
                  { label: 'Logout', href: logoutHref ?? logoutUrl, target: '_top' },
                ]}
                key="account-details"
              />,
            ]}
            orientation="horizontal"
          />
        </nav>
      )}
    </header>
  );
}
